// urlStateUtils.js
// Utility functions for encoding and decoding palette state to/from URL

/**
 * Encodes palette state to URL format
 * @param {Object} paletteState - The current palette state
 * @returns {string} - URL path
 */
export const encodeStateToURL = (paletteState) => {
  const {
    color,
    colorDuo,
    colorTrio,
    showDuo,
    showTrio,
    paletteModifiers,
    paletteModifiersDuo,
    paletteModifiersTrio,
    squareNames,
    tailwindFamily,        // ADD THIS
    tailwindFamilyDuo,     // ADD THIS
    tailwindFamilyTrio 
  } = paletteState;

  let urlParts = [];

  // Add base color (remove #)
  if (color) {
    urlParts.push(color.replace('#', ''));
  }

  if (tailwindFamily) {
    // URL-encode the family name and add TW prefix
    urlParts.push(`TW${encodeURIComponent(tailwindFamily)}`);
  }

  // Helper function to encode scale modifiers
  const encodeScaleModifiers = (modifiers, prefix = '') => {
    const scaleParts = [];

    if (modifiers.scale.hue !== 0) {
      scaleParts.push(`${prefix}SH${modifiers.scale.hue > 0 ? '+' : ''}${modifiers.scale.hue}`);
    }

    if (modifiers.scale.saturationLeft !== 0) {
      scaleParts.push(`${prefix}SSL${modifiers.scale.saturationLeft > 0 ? '+' : ''}${modifiers.scale.saturationLeft}`);
    }

    if (modifiers.scale.saturationRight !== 0) {
      scaleParts.push(`${prefix}SSR${modifiers.scale.saturationRight > 0 ? '+' : ''}${modifiers.scale.saturationRight}`);
    }

    if (modifiers.scale.lightnessLeft !== 0) {
      scaleParts.push(`${prefix}SLL${modifiers.scale.lightnessLeft > 0 ? '+' : ''}${modifiers.scale.lightnessLeft}`);
    }

    if (modifiers.scale.lightnessRight !== 0) {
      scaleParts.push(`${prefix}SLR${modifiers.scale.lightnessRight > 0 ? '+' : ''}${modifiers.scale.lightnessRight}`);
    }

    return scaleParts;
  };

  // Helper function to encode individual variable modifiers
  const encodeVariableModifiers = (modifiers, prefix = '') => {
    const variableParts = [];

    Object.entries(modifiers.individual || {}).forEach(([index, edits]) => {
      if (!edits || Object.keys(edits).length === 0) return;

      const variableName = squareNames[parseInt(index)] || index;
      let variablePart = `${prefix}V${variableName}`;

      // Add HSL values in order: H, S, L
      if (edits.h !== undefined) {
        variablePart += `H${Math.round(edits.h)}`;
      }
      if (edits.s !== undefined) {
        variablePart += `S${Math.round(edits.s)}`;
      }
      if (edits.l !== undefined) {
        variablePart += `L${Math.round(edits.l)}`;
      }

      variableParts.push(variablePart);
    });

    return variableParts;
  };

  // Add mono palette modifiers
  if (paletteModifiers) {
    urlParts.push(...encodeScaleModifiers(paletteModifiers));
    urlParts.push(...encodeVariableModifiers(paletteModifiers));
  }

  // Add duo palette if present
  if (showDuo && colorDuo) {
    urlParts.push(`D${colorDuo.replace('#', '')}`);

    if (tailwindFamilyDuo) {
      urlParts.push(`DTW${encodeURIComponent(tailwindFamilyDuo)}`);
    }

    if (paletteModifiersDuo) {
      urlParts.push(...encodeScaleModifiers(paletteModifiersDuo, 'D'));
      urlParts.push(...encodeVariableModifiers(paletteModifiersDuo, 'D'));
    }
  }

  // Add trio palette if present
  if (showTrio && colorTrio) {
    urlParts.push(`T${colorTrio.replace('#', '')}`);

    if (tailwindFamilyTrio) {
      urlParts.push(`TTW${encodeURIComponent(tailwindFamilyTrio)}`);
    }

    if (paletteModifiersTrio) {
      urlParts.push(...encodeScaleModifiers(paletteModifiersTrio, 'T'));
      urlParts.push(...encodeVariableModifiers(paletteModifiersTrio, 'T'));
    }
  }

  return urlParts.length > 0 ? `/generator/${urlParts.join('&')}` : '/generator';
};

/**
 * Decodes URL to palette state
 * @param {string} urlPath - The URL path to decode
 * @param {Array} squareNames - Array of square names for index mapping
 * @returns {Object} - Decoded palette state
 */
export const decodeURLToState = (urlPath, squareNames = ["50", "100", "200", "300", "400", "500", "600", "700", "800", "900", "950"]) => {
  // Remove leading slash and split by &
  const path = urlPath.replace(/^\/generator\//, '').replace(/^\//, '');
  if (!path) return null;

  const parts = path.split('&');
  if (parts.length === 0) return null;

  const state = {
    color: null,
    colorDuo: null,
    colorTrio: null,
    showDuo: false,
    showTrio: false,
    paletteModifiers: {
      scale: {
        hue: 0,
        lightnessLeft: 0,
        lightnessRight: 0,
        saturationLeft: 0,
        saturationRight: 0
      },
      individual: {}
    },
    paletteModifiersDuo: {
      scale: {
        hue: 0,
        lightnessLeft: 0,
        lightnessRight: 0,
        saturationLeft: 0,
        saturationRight: 0
      },
      individual: {}
    },
    paletteModifiersTrio: {
      scale: {
        hue: 0,
        lightnessLeft: 0,
        lightnessRight: 0,
        saturationLeft: 0,
        saturationRight: 0
      },
      individual: {}
    },
    tailwindFamily: null,      // ADD THIS
    tailwindFamilyDuo: null,   // ADD THIS
    tailwindFamilyTrio: null
  };

  // Helper function to get the target modifiers object
  const getModifiers = (prefix) => {
    switch (prefix) {
      case 'D': return state.paletteModifiersDuo;
      case 'T': return state.paletteModifiersTrio;
      default: return state.paletteModifiers;
    }
  };

  // Helper function to parse scale modifications
  const parseScaleModifier = (part, prefix = '') => {

    const modifiers = getModifiers(prefix);
    const value = part.replace(prefix, '');


    if (value.startsWith('SH')) {
      modifiers.scale.hue = parseInt(value.substring(2)) || 0;
    } else if (value.startsWith('SSL')) {
      modifiers.scale.saturationLeft = parseInt(value.substring(3)) || 0;
    } else if (value.startsWith('SSR')) {
      modifiers.scale.saturationRight = parseInt(value.substring(3)) || 0;
    } else if (value.startsWith('SLL')) {
      modifiers.scale.lightnessLeft = parseInt(value.substring(3)) || 0;
    } else if (value.startsWith('SLR')) {
      modifiers.scale.lightnessRight = parseInt(value.substring(3)) || 0;
    }
  };

  // Helper function to parse variable modifications
  const parseVariableModifier = (part, prefix = '') => {
    const modifiers = getModifiers(prefix);
    const value = part.replace(prefix, '');

    if (!value.startsWith('V')) return;

    // Extract variable name and modifications
    const vPart = value.substring(1); // Remove 'V'

    // Find where the variable name ends and modifications begin
    let variableName = '';
    let modifications = '';

    for (let i = 0; i < vPart.length; i++) {
      const char = vPart[i];
      if (char === 'H' || char === 'S' || char === 'L') {
        variableName = vPart.substring(0, i);
        modifications = vPart.substring(i);
        break;
      }
    }

    if (!variableName || !modifications) return;

    // Find the index of this variable name
    const variableIndex = squareNames.indexOf(variableName);
    if (variableIndex === -1) return;

    // Initialize individual edits for this variable
    if (!modifiers.individual[variableIndex]) {
      modifiers.individual[variableIndex] = {};
    }

    // Parse H, S, L values
    // const hMatch = modifications.match(/H(\d+)/);
    // const sMatch = modifications.match(/S(\d+)/);
    // const lMatch = modifications.match(/L(\d+)/);

    const hMatch = modifications.match(/H(-?\d+)/);
    const sMatch = modifications.match(/S(-?\d+)/);
    const lMatch = modifications.match(/L(-?\d+)/);

    if (hMatch) {
      modifiers.individual[variableIndex].h = parseInt(hMatch[1]);
    }
    if (sMatch) {
      modifiers.individual[variableIndex].s = parseInt(sMatch[1]);
    }
    if (lMatch) {
      modifiers.individual[variableIndex].l = parseInt(lMatch[1]);
    }
  };

  // Process each part
  parts.forEach((part, index) => {

    if (index === 0) {
      // First part should be the base color (hex without #)
      if (/^[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/.test(part)) {
        state.color = `#${part}`;
      }
      return;
    }

    if (part.startsWith('TW') && !part.startsWith('TV')) {
      state.tailwindFamily = decodeURIComponent(part.substring(2));
      return;
    }


    // Check if this is a duo color
    if (part.startsWith('D') && /^D[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/.test(part)) {
      state.colorDuo = `#${part.substring(1)}`;
      state.showDuo = true;
      return;
    }

    if (part.startsWith('DTW')) {
      state.tailwindFamilyDuo = decodeURIComponent(part.substring(3));
      return;
    }

    // Check if this is a trio color
    if (part.startsWith('T') && /^T[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/.test(part)) {
      state.colorTrio = `#${part.substring(1)}`;
      state.showTrio = true;
      return;
    }

    if (part.startsWith('TTW')) {
      state.tailwindFamilyTrio = decodeURIComponent(part.substring(3));
      return;
    }

    // Parse scale modifications - FIXED REGEX TO INCLUDE SATURATION
    if (part.match(/^S[HLS]/)) {  // CHANGED: Added 'S' to catch SSL/SSR
      parseScaleModifier(part);
    } else if (part.match(/^DS[HLS]/)) {  // CHANGED: Added 'S'
      parseScaleModifier(part, 'D');
    } else if (part.match(/^TS[HLS]/)) {  // CHANGED: Added 'S'
      parseScaleModifier(part, 'T');
    }

    // Parse variable modifications
    else if (part.startsWith('V')) {
      parseVariableModifier(part);
    } else if (part.startsWith('DV')) {
      parseVariableModifier(part, 'D');
    } else if (part.startsWith('TV')) {
      parseVariableModifier(part, 'T');
    }
  });

  return state;
};

/**
 * Updates the browser URL without causing a page reload
 * @param {string} newPath - The new URL path
 * @param {boolean} replace - Whether to replace the current history entry
 */
export const updateURL = (newPath, replace = false) => {
  if (typeof window !== 'undefined' && window.history) {
    const method = replace ? 'replaceState' : 'pushState';
    window.history[method]({}, '', newPath);
  }
};

/**
 * Debounced URL update to prevent excessive history entries
 */
let urlUpdateTimeout;
export const debouncedUpdateURL = (newPath, delay = 50) => { // Reduced delay
  clearTimeout(urlUpdateTimeout);
  urlUpdateTimeout = setTimeout(() => {
    updateURL(newPath, true); // Use replace to avoid cluttering history
  }, delay);
};